// YERİ: src/app/domains/website/core/site-texts.store.ts
//
// VİTRİNİN SAYFA-DIŞI METİNLERİ — 404 sayfası, blog başlığı, site geneli
// yapısal verideki (JSON-LD) tanıtım cümleleri.
//
// Bu metinler bir CMS SAYFASINA ait değil (404 ve blog, CMS sayfası olmadan
// çizilir), o yüzden bir dönem kodda İngilizce sabitti: Türkçe vitrinde de
// "Page not found" yazıyordu ve panelden değiştirilemiyordu. Artık Ana Şablon'un
// (dil başına) "Site Metinleri" bölümünden (mkt-site-texts) geliyor; web-layout
// çerçeveyi kurarken bu bölümün config'ini buraya yazar.
//
// Alan boşsa ZİYARETÇİNİN DİLİNDEKİ yedek metin kullanılır (SITE_TEXT_BY_LANG);
// o dil listede yoksa İngilizce. Bölüm hiç eklenmemiş bir dilde bile site tek
// dilde görünür.

import { Injectable, inject, signal } from '@angular/core';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from './language.store';

export const SITE_TEXT_DEFAULTS = {
  notFoundTitle: 'Page not found',
  notFoundText: 'The page you are looking for doesn’t exist or has moved.',
  notFoundCta: 'Back to home',
  blogTitle: 'Blog',
  blogSubtitle: 'Product news, playbooks and industry deep dives.',
  blogSeoDescription: 'Product news, playbooks and industry deep dives from Melbarrel.',
  blogEmpty: 'No posts yet.',
  blogBack: 'Blog',
  orgDescription:
    'Business management software for service businesses: CRM, bookings, staff, '
    + 'invoicing and a website builder in one system.',
  appDescription:
    'Melbarrel combines customer records, scheduling, staff management, quoting, '
    + 'invoicing, online payments and a website builder into one platform, with 30 '
    + 'industry-specific modules.',
} as const;

export type SiteTextKey = keyof typeof SITE_TEXT_DEFAULTS;

/**
 * DİL BAŞINA YEDEK — bölüm hiç eklenmemiş ya da o dilde boş bırakılmışsa.
 *
 * Eskiden tek yedek vardı ve İngilizceydi: Türkçe vitrinde olmayan bir adrese
 * giden ziyaretçi, Türkçe bir sayfanın ortasında "Page not found / Back to
 * home" görüyordu. Kiracı panelden doldurunca düzeliyordu ama doldurana kadar
 * site iki dilli görünüyordu; yedeğin de ziyaretçinin dilinde olması gerekir.
 *
 * Buradaki metinler İÇERİK DEĞİL, yedek: panelden girilen değer her zaman
 * önce gelir. Listede olmayan bir dil İngilizceye düşer (vitrinin yayın dili).
 */
const SITE_TEXT_BY_LANG: Record<string, Partial<Record<SiteTextKey, string>>> = {
  tr: {
    notFoundTitle: 'Sayfa bulunamadı',
    notFoundText: 'Aradığınız sayfa yok ya da taşınmış.',
    notFoundCta: 'Ana sayfaya dön',
    blogTitle: 'Blog',
    blogSubtitle: 'Ürün haberleri, uygulama rehberleri ve sektör yazıları.',
    blogSeoDescription: "Melbarrel'dan ürün haberleri, uygulama rehberleri ve sektör yazıları.",
    blogEmpty: 'Henüz yazı yok.',
    blogBack: 'Blog',
    orgDescription:
      'Hizmet işletmeleri için işletme yönetim yazılımı: CRM, randevu, personel, '
      + 'faturalama ve web sitesi kurucusu tek sistemde.',
    appDescription:
      'Melbarrel; müşteri kayıtlarını, randevuyu, personel yönetimini, teklif ve '
      + 'faturalamayı, online ödemeyi ve web sitesi kurucusunu 30 sektöre özel '
      + 'modülle tek platformda birleştirir.',
  },
  de: {
    notFoundTitle: 'Seite nicht gefunden',
    notFoundText: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    notFoundCta: 'Zur Startseite',
    blogTitle: 'Blog',
    blogSubtitle: 'Produktneuheiten, Praxisleitfäden und Brancheneinblicke.',
    blogSeoDescription: 'Produktneuheiten, Praxisleitfäden und Brancheneinblicke von Melbarrel.',
    blogEmpty: 'Noch keine Beiträge.',
    blogBack: 'Blog',
    orgDescription:
      'Unternehmenssoftware für Dienstleister: CRM, Terminbuchung, Personal, '
      + 'Rechnungsstellung und Website-Baukasten in einem System.',
    appDescription:
      'Melbarrel vereint Kundendaten, Terminplanung, Personalverwaltung, Angebote, '
      + 'Rechnungen, Online-Zahlungen und einen Website-Baukasten in einer Plattform '
      + 'mit 30 branchenspezifischen Modulen.',
  },
  fr: {
    notFoundTitle: 'Page introuvable',
    notFoundText: "La page que vous cherchez n'existe pas ou a été déplacée.",
    notFoundCta: "Retour à l'accueil",
    blogTitle: 'Blog',
    blogSubtitle: 'Actualités produit, guides pratiques et analyses sectorielles.',
    blogSeoDescription: 'Actualités produit, guides pratiques et analyses sectorielles de Melbarrel.',
    blogEmpty: 'Aucun article pour le moment.',
    blogBack: 'Blog',
    orgDescription:
      'Logiciel de gestion pour les entreprises de services : CRM, réservations, '
      + 'personnel, facturation et créateur de site web dans un seul système.',
    appDescription:
      'Melbarrel réunit fichiers clients, planning, gestion du personnel, devis, '
      + 'facturation, paiements en ligne et créateur de site web sur une seule '
      + 'plateforme, avec 30 modules sectoriels.',
  },
  it: {
    notFoundTitle: 'Pagina non trovata',
    notFoundText: 'La pagina che cerchi non esiste o è stata spostata.',
    notFoundCta: 'Torna alla home',
    blogTitle: 'Blog',
    blogSubtitle: 'Novità di prodotto, guide pratiche e approfondimenti di settore.',
    blogSeoDescription: 'Novità di prodotto, guide pratiche e approfondimenti di settore da Melbarrel.',
    blogEmpty: 'Nessun articolo per ora.',
    blogBack: 'Blog',
    orgDescription:
      'Software gestionale per le imprese di servizi: CRM, prenotazioni, personale, '
      + 'fatturazione e costruttore di siti web in un unico sistema.',
    appDescription:
      'Melbarrel unisce anagrafiche clienti, agenda, gestione del personale, preventivi, '
      + 'fatturazione, pagamenti online e costruttore di siti web in una sola piattaforma, '
      + 'con 30 moduli settoriali.',
  },
  es: {
    notFoundTitle: 'Página no encontrada',
    notFoundText: 'La página que busca no existe o se ha movido.',
    notFoundCta: 'Volver al inicio',
    blogTitle: 'Blog',
    blogSubtitle: 'Novedades del producto, guías prácticas y análisis del sector.',
    blogSeoDescription: 'Novedades del producto, guías prácticas y análisis del sector de Melbarrel.',
    blogEmpty: 'Todavía no hay entradas.',
    blogBack: 'Blog',
    orgDescription:
      'Software de gestión para empresas de servicios: CRM, reservas, personal, '
      + 'facturación y creador de sitios web en un único sistema.',
    appDescription:
      'Melbarrel reúne fichas de clientes, agenda, gestión de personal, presupuestos, '
      + 'facturación, pagos en línea y creador de sitios web en una sola plataforma, '
      + 'con 30 módulos sectoriales.',
  },
  pt: {
    notFoundTitle: 'Página não encontrada',
    notFoundText: 'A página que procura não existe ou foi movida.',
    notFoundCta: 'Voltar ao início',
    blogTitle: 'Blog',
    blogSubtitle: 'Novidades do produto, guias práticos e análises do setor.',
    blogSeoDescription: 'Novidades do produto, guias práticos e análises do setor da Melbarrel.',
    blogEmpty: 'Ainda não há artigos.',
    blogBack: 'Blog',
    orgDescription:
      'Software de gestão para empresas de serviços: CRM, marcações, pessoal, '
      + 'faturação e construtor de sites num único sistema.',
    appDescription:
      'A Melbarrel reúne fichas de clientes, agenda, gestão de pessoal, orçamentos, '
      + 'faturação, pagamentos online e construtor de sites numa só plataforma, '
      + 'com 30 módulos setoriais.',
  },
  ru: {
    notFoundTitle: 'Страница не найдена',
    notFoundText: 'Искомой страницы не существует или она перемещена.',
    notFoundCta: 'На главную',
    blogTitle: 'Блог',
    blogSubtitle: 'Новости продукта, практические руководства и отраслевые разборы.',
    blogSeoDescription: 'Новости продукта, практические руководства и отраслевые разборы от Melbarrel.',
    blogEmpty: 'Записей пока нет.',
    blogBack: 'Блог',
    orgDescription:
      'Программа управления бизнесом для сферы услуг: CRM, запись, персонал, '
      + 'выставление счетов и конструктор сайта в одной системе.',
    appDescription:
      'Melbarrel объединяет клиентскую базу, расписание, управление персоналом, '
      + 'коммерческие предложения, счета, онлайн-оплату и конструктор сайта в одной '
      + 'платформе с 30 отраслевыми модулями.',
  },
  ar: {
    notFoundTitle: 'لم يُعثر على الصفحة',
    notFoundText: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
    notFoundCta: 'العودة إلى الصفحة الرئيسية',
    blogTitle: 'المدوّنة',
    blogSubtitle: 'أخبار المنتج وأدلة عملية وتحليلات قطاعية.',
    blogSeoDescription: 'أخبار المنتج وأدلة عملية وتحليلات قطاعية من Melbarrel.',
    blogEmpty: 'لا توجد مقالات بعد.',
    blogBack: 'المدوّنة',
    orgDescription:
      'برنامج إدارة أعمال لشركات الخدمات: إدارة العملاء والحجوزات والموظفين '
      + 'والفوترة ومنشئ موقع إلكتروني في نظام واحد.',
    appDescription:
      'يجمع Melbarrel سجلات العملاء والجدولة وإدارة الموظفين وعروض الأسعار والفوترة '
      + 'والدفع الإلكتروني ومنشئ المواقع في منصة واحدة، مع 30 وحدة مخصّصة للقطاعات.',
  },
  fa: {
    notFoundTitle: 'صفحه یافت نشد',
    notFoundText: 'صفحه‌ای که دنبال آن هستید وجود ندارد یا جابه‌جا شده است.',
    notFoundCta: 'بازگشت به صفحهٔ اصلی',
    blogTitle: 'وبلاگ',
    blogSubtitle: 'اخبار محصول، راهنماهای کاربردی و تحلیل‌های صنعتی.',
    blogSeoDescription: 'اخبار محصول، راهنماهای کاربردی و تحلیل‌های صنعتی از Melbarrel.',
    blogEmpty: 'هنوز مطلبی منتشر نشده است.',
    blogBack: 'وبلاگ',
    orgDescription:
      'نرم‌افزار مدیریت کسب‌وکار برای شرکت‌های خدماتی: مدیریت مشتری، نوبت‌دهی، '
      + 'کارکنان، صدور فاکتور و سایت‌ساز در یک سامانه.',
    appDescription:
      'Melbarrel پروندهٔ مشتریان، زمان‌بندی، مدیریت کارکنان، پیش‌فاکتور، صدور فاکتور، '
      + 'پرداخت آنلاین و سایت‌ساز را با ۳۰ ماژول تخصصی صنعتی در یک پلتفرم گرد می‌آورد.',
  },
  ko: {
    notFoundTitle: '페이지를 찾을 수 없습니다',
    notFoundText: '찾으시는 페이지가 존재하지 않거나 이동되었습니다.',
    notFoundCta: '홈으로 돌아가기',
    blogTitle: '블로그',
    blogSubtitle: '제품 소식, 실무 가이드, 업계 심층 분석.',
    blogSeoDescription: 'Melbarrel의 제품 소식, 실무 가이드, 업계 심층 분석.',
    blogEmpty: '아직 게시물이 없습니다.',
    blogBack: '블로그',
    orgDescription:
      '서비스 업종을 위한 비즈니스 관리 소프트웨어: CRM, 예약, 직원, '
      + '청구, 웹사이트 빌더를 하나의 시스템에.',
    appDescription:
      'Melbarrel은 고객 기록, 일정 관리, 직원 관리, 견적, 청구, 온라인 결제, '
      + '웹사이트 빌더를 30개의 업종별 모듈과 함께 하나의 플랫폼으로 통합합니다.',
  },
  vi: {
    notFoundTitle: 'Không tìm thấy trang',
    notFoundText: 'Trang bạn đang tìm không tồn tại hoặc đã được chuyển đi.',
    notFoundCta: 'Về trang chủ',
    blogTitle: 'Blog',
    blogSubtitle: 'Tin tức sản phẩm, hướng dẫn thực hành và phân tích chuyên sâu theo ngành.',
    blogSeoDescription: 'Tin tức sản phẩm, hướng dẫn thực hành và phân tích chuyên sâu theo ngành từ Melbarrel.',
    blogEmpty: 'Chưa có bài viết nào.',
    blogBack: 'Blog',
    orgDescription:
      'Phần mềm quản lý cho doanh nghiệp dịch vụ: CRM, đặt lịch, nhân sự, '
      + 'hóa đơn và công cụ tạo website trong một hệ thống.',
    appDescription:
      'Melbarrel kết hợp hồ sơ khách hàng, lịch hẹn, quản lý nhân sự, báo giá, '
      + 'hóa đơn, thanh toán trực tuyến và công cụ tạo website trong một nền tảng, '
      + 'với 30 mô-đun chuyên biệt theo ngành.',
  },
  ja: {
    notFoundTitle: 'ページが見つかりません',
    notFoundText: 'お探しのページは存在しないか、移動した可能性があります。',
    notFoundCta: 'ホームに戻る',
    blogTitle: 'ブログ',
    blogSubtitle: '製品ニュース、実践ガイド、業界の深掘り記事。',
    blogSeoDescription: 'Melbarrel の製品ニュース、実践ガイド、業界の深掘り記事。',
    blogEmpty: 'まだ記事がありません。',
    blogBack: 'ブログ',
    orgDescription:
      'サービス業向けの業務管理ソフトウェア：CRM、予約、スタッフ、'
      + '請求、ウェブサイトビルダーをひとつのシステムで。',
    appDescription:
      'Melbarrel は顧客記録、スケジュール、スタッフ管理、見積もり、請求、'
      + 'オンライン決済、ウェブサイトビルダーを、30 の業種別モジュールとともに'
      + 'ひとつのプラットフォームに統合します。',
  },
  zh: {
    notFoundTitle: '未找到页面',
    notFoundText: '您访问的页面不存在或已被移动。',
    notFoundCta: '返回首页',
    blogTitle: '博客',
    blogSubtitle: '产品动态、实用指南与行业深度解读。',
    blogSeoDescription: '来自 Melbarrel 的产品动态、实用指南与行业深度解读。',
    blogEmpty: '暂无文章。',
    blogBack: '博客',
    orgDescription:
      '面向服务型企业的经营管理软件：CRM、预约、员工、'
      + '开票和网站搭建工具集于一个系统。',
    appDescription:
      'Melbarrel 将客户档案、排程、员工管理、报价、开票、在线支付和网站搭建工具'
      + '整合到一个平台中，并提供 30 个行业专属模块。',
  },
  'zh-tw': {
    notFoundTitle: '找不到頁面',
    notFoundText: '您要找的頁面不存在或已移動。',
    notFoundCta: '返回首頁',
    blogTitle: '部落格',
    blogSubtitle: '產品動態、實用指南與產業深度解析。',
    blogSeoDescription: '來自 Melbarrel 的產品動態、實用指南與產業深度解析。',
    blogEmpty: '目前還沒有文章。',
    blogBack: '部落格',
    orgDescription:
      '專為服務業打造的經營管理軟體：CRM、預約、員工、'
      + '開立發票與網站建置工具，整合於單一系統。',
    appDescription:
      'Melbarrel 將客戶資料、排程、員工管理、報價、開立發票、線上付款與網站建置工具'
      + '整合於單一平台，並提供 30 個產業專屬模組。',
  },
  th: {
    notFoundTitle: 'ไม่พบหน้านี้',
    notFoundText: 'หน้าที่คุณกำลังค้นหาไม่มีอยู่หรือถูกย้ายไปแล้ว',
    notFoundCta: 'กลับสู่หน้าแรก',
    blogTitle: 'บล็อก',
    blogSubtitle: 'ข่าวสารผลิตภัณฑ์ คู่มือการใช้งาน และบทวิเคราะห์เจาะลึกรายอุตสาหกรรม',
    blogSeoDescription: 'ข่าวสารผลิตภัณฑ์ คู่มือการใช้งาน และบทวิเคราะห์เจาะลึกรายอุตสาหกรรมจาก Melbarrel',
    blogEmpty: 'ยังไม่มีบทความ',
    blogBack: 'บล็อก',
    orgDescription:
      'ซอฟต์แวร์บริหารธุรกิจสำหรับธุรกิจบริการ: CRM การจองนัดหมาย พนักงาน '
      + 'การออกใบแจ้งหนี้ และเครื่องมือสร้างเว็บไซต์ในระบบเดียว',
    appDescription:
      'Melbarrel รวมข้อมูลลูกค้า การจัดตารางนัดหมาย การจัดการพนักงาน ใบเสนอราคา '
      + 'การออกใบแจ้งหนี้ การชำระเงินออนไลน์ และเครื่องมือสร้างเว็บไซต์ไว้ในแพลตฟอร์มเดียว '
      + 'พร้อมโมดูลเฉพาะอุตสาหกรรม 30 โมดูล',
  },
};

/** Ana Şablon'daki bölüm tipi. */
export const SITE_TEXTS_BLOCK = 'mkt-site-texts';

@Injectable({ providedIn: 'root' })
export class MktSiteTexts {
  private lang = inject(LanguageStore);

  /** Aktif dilin "Site Metinleri" bölümünün config'i (yoksa boş). */
  readonly config = signal<SiteConfig>({});

  text(key: SiteTextKey): string {
    // Full code first ('zh-tw' is its own script), then the base language
    // ('en-ca' → 'en').
    const tam = (this.lang.current() || 'en').toLowerCase();
    const yedek = SITE_TEXT_BY_LANG[tam]?.[key]
      ?? SITE_TEXT_BY_LANG[tam.split('-')[0]]?.[key]
      ?? (SITE_TEXT_DEFAULTS[key] as string);
    return cfg(this.config(), key, yedek);
  }
}
